// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useState } from "react";
import { MailWarning, Plus, Send, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import UserAvatar from "@/components/common/UserAvatar";
import { formatRelativeTime } from "@/utils/common.utils";
import { participantRoleLabel } from "@/utils/deals-view.utils";

/** How each participant state reads. `active` means they've accepted and can
 *  actually open the deal; the others are invitations in flight. */
const STATUS_TONE = {
  active: { tone: "success", label: "active" },
  pending: { tone: "warning", label: "invited" },
  expired: { tone: "danger", label: "invite expired" },
};

function ParticipantCard({ participant, deal, onResend, resending }) {
  // Rows carried over from before invitations were wired have no status; they
  // are people who genuinely have access, so they read as active.
  const status = participant.status || "active";
  const badge = STATUS_TONE[status] || STATUS_TONE.active;
  const isPending = status === "pending" || status === "expired";
  const deliveryFailed = isPending && participant.last_email_status
    && participant.last_email_status !== "sent";

  return (
    <article>
      <p className="mb-3 text-xs font-medium uppercase tracking-[0.16em] text-dash-faint">
        {participantRoleLabel(participant, deal)}
      </p>
      <div className="flex min-h-[132px] items-start gap-3 rounded-xl border border-dash-border bg-white px-4 py-5 transition-colors hover:border-dash-faint/60 sm:items-center sm:gap-4 sm:px-5">
        <UserAvatar name={participant.name} src={participant.photo_url} className="size-12 shrink-0" fallbackClassName="text-sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-dash-ink">{participant.name || "Unnamed participant"}</h3>
            <StatusBadge tone={badge.tone} className="px-2 py-0.5 text-[10px] font-medium">{badge.label}</StatusBadge>
          </div>
          <p className="mt-1 truncate text-xs text-dash-faint">{participant.email || participant.company || "No email added"}</p>

          {isPending && (
            <p className="mt-1 truncate text-xs text-dash-muted">
              {status === "expired"
                ? "Their invite link has expired — resend to issue a new one."
                : `Invited ${formatRelativeTime(participant.invited_at, "just now")} · waiting for them to accept`}
            </p>
          )}
          {deliveryFailed && (
            <p className="mt-1 flex items-center gap-1 text-xs text-dash-rust">
              <MailWarning className="size-3 shrink-0" /> We couldn&apos;t deliver that email — try resending.
            </p>
          )}

          {/* Only pending invitations have anything to resend. The old card
              showed this on every row with a tooltip apologising that it didn't
              work, alongside an "Edit access" button wired to nothing. */}
          {isPending && onResend && (
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <Button
                variant="seamless"
                type="button"
                onClick={() => onResend(participant)}
                loading={resending}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-dash-ink hover:text-primary"
                data-testid={`resend-invite-${participant.invitation_id}`}
              >
                <Send className="size-3.5" /> Resend invite
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/** Who the client sees on their contact card and talks to in chat. The server
 *  sends only the people allowed to hold the seat, primary agent first. */
function ClientContactPicker({ deal, onChange, saving }) {
  // The save is a PATCH plus a full deal refetch, so the pick is shown right
  // away and dropped once the server value moves or the save fails.
  const [picked, setPicked] = useState(null);
  useEffect(() => setPicked(null), [deal.client_contact_user_id]);
  const options = deal.client_contact_options || [];
  const primary = options.find((o) => o.is_primary);
  const current = picked || (options.some((o) => o.user_id === deal.client_contact_user_id)
    ? deal.client_contact_user_id
    : primary?.user_id);
  if (options.length < 2) return null;

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-dash-border bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5" data-testid="client-contact-picker">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-dash-ink">Client&apos;s point of contact</p>
        <p className="mt-0.5 text-xs text-dash-muted">Shown on the client&apos;s contact card and in their messages.</p>
      </div>
      <Select
        value={current}
        disabled={saving}
        onValueChange={(userId) => {
          setPicked(userId);
          onChange(userId === primary?.user_id ? "" : userId, { onError: () => setPicked(null) });
        }}
      >
        <SelectTrigger className="h-auto w-full rounded-xl py-2 pl-2.5 pr-3 text-left sm:w-80 [&>span]:line-clamp-none [&>span]:min-w-0" data-testid="client-contact-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.user_id} value={o.user_id} className="py-2 pl-2 pr-8">
              <span className="flex min-w-0 items-center gap-2.5">
                <UserAvatar name={o.name || o.email} src={o.photo_url} className="size-8 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-dash-ink">{o.name || o.email}</span>
                  <span className="block truncate text-xs text-dash-muted">
                    {o.role_label}{o.is_primary && " · Default"}
                  </span>
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function TeamTab({ deal, canAct, onAddParticipant, onResendInvite, resending, inviteDisabledReason, onChangeClientContact, savingClientContact }) {
  const participants = deal.participants || [];
  const inviteBlocked = Boolean(inviteDisabledReason);

  return (
    <section aria-label="People and access">
      {canAct && onChangeClientContact && (
        <ClientContactPicker deal={deal} onChange={onChangeClientContact} saving={savingClientContact} />
      )}
      {canAct && (
        <div className="mb-5 flex justify-end">
          <Button
            type="button"
            variant="dashboardPrimary"
            onClick={onAddParticipant}
            disabled={inviteBlocked}
            title={inviteDisabledReason || undefined}
            className="w-full sm:w-auto"
            data-testid="add-participant-btn"
          >
            <Plus data-icon="inline-start" /> Invite to deal
          </Button>
        </div>
      )}

      {participants.length ? (
        <div className="grid gap-x-4 gap-y-7 md:grid-cols-2">
          {participants.map((participant, index) => (
            <ParticipantCard
              key={participant.user_id || participant.invitation_id || participant.email || `${participant.name}-${index}`}
              participant={participant}
              deal={deal}
              onResend={onResendInvite}
              resending={resending}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Users}
          title="No one else on this deal yet"
          description="Invite the client, the other side's agent, a lender or an attorney. They'll get an email and can open this deal once they accept."
        />
      )}
    </section>
  );
}
